import { Skeleton, Stack } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { Button } from "@src/appV2/redesign/components/Button";
import { SettingsSection } from "@src/appV2/redesign/components/SettingsSection";
import { Text } from "@src/appV2/redesign/components/Text";
import { SettingNumberInput } from "@src/appV2/redesign/Settings/components/SettingNumberInput";
import { SettingToggle } from "@src/appV2/redesign/Settings/components/SettingToggle";

import { type RosterWindowRules } from "@clipboard-health/contract-backend-main";

interface RosterWindowEditorViewProps {
  settings: RosterWindowRules;
  allowShiftCancellation: boolean;
  isSaving: boolean;
  onChange: (key: keyof RosterWindowRules, value: number) => void;
  onToggleShiftCancellation: (checked: boolean) => void;
}

export function RosterWindowEditorView(props: RosterWindowEditorViewProps) {
  const { settings, allowShiftCancellation, isSaving, onChange, onToggleShiftCancellation } = props;
  const { tu } = useTu("settings");

  return (
    <SettingsSection
      title={tu("Roster window settings")}
      description={tu(
        "Controls when each PRN shift posted from this workplace opens to Clipboard professionals."
      )}
    >
      <SettingToggle
        title={tu("Allow Roster professionals to cancel shifts")}
        subtitle={tu(
          "When off, PRN professionals can't cancel a shift they've claimed, and the cancel button is hidden in their app."
        )}
        checked={allowShiftCancellation}
        disabled={isSaving}
        onChange={onToggleShiftCancellation}
      />
      <SettingNumberInput
        title={tu("Long-lead threshold")}
        subtitle={tu("Shifts posted more than this many days before start use the long-lead rule.")}
        value={settings.longLeadThresholdDays}
        min={0}
        max={365}
        unit={tu("days")}
        disabled={isSaving}
        onChange={(value) => onChange("longLeadThresholdDays", value)}
      />
      <SettingNumberInput
        title={tu("Long-lead open before start")}
        subtitle={tu("For long-lead shifts, open to Clipboard this many days before shift start.")}
        value={settings.longLeadOpenDaysBeforeStart}
        min={0}
        max={365}
        unit={tu("days")}
        disabled={isSaving}
        onChange={(value) => onChange("longLeadOpenDaysBeforeStart", value)}
      />
      <SettingNumberInput
        title={tu("Medium-lead open after posting")}
        subtitle={tu("For medium-lead shifts, open to Clipboard this many days after posting.")}
        value={settings.shortLeadOpenDaysAfterPosting}
        min={0}
        max={365}
        unit={tu("days")}
        disabled={isSaving}
        onChange={(value) => onChange("shortLeadOpenDaysAfterPosting", value)}
      />
      <SettingNumberInput
        title={tu("Urgent threshold")}
        subtitle={tu(
          "Shifts posted within this many days of start are urgent and open to Clipboard immediately."
        )}
        value={settings.urgentThresholdDays}
        min={0}
        max={365}
        unit={tu("days")}
        disabled={isSaving}
        onChange={(value) => onChange("urgentThresholdDays", value)}
      />
    </SettingsSection>
  );
}

export function RosterWindowEditorLoadingView() {
  const { tu } = useTu("settings");
  return (
    <SettingsSection
      title={tu("Roster window settings")}
      description={tu(
        "Controls when each PRN shift posted from this workplace opens to Clipboard professionals."
      )}
    >
      <Stack spacing={2} data-testid="roster-window-editor-loading" sx={{ paddingY: 2 }}>
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} variant="rounded" height={36} />
        ))}
      </Stack>
    </SettingsSection>
  );
}

export function RosterWindowEditorErrorView({ onRetry }: { onRetry: () => void }) {
  const { tu } = useTu("settings");
  return (
    <SettingsSection
      title={tu("Roster window settings")}
      description={tu(
        "Controls when each PRN shift posted from this workplace opens to Clipboard professionals."
      )}
    >
      <Stack
        spacing={2}
        data-testid="roster-window-editor-error"
        sx={{ alignItems: "flex-start", paddingY: 2 }}
      >
        <Text variant="body2">
          {tu("Failed to load the roster window settings for this workplace.")}
        </Text>
        <Button variant="outlined" size="small" onClick={onRetry}>
          {tu("Try again")}
        </Button>
      </Stack>
    </SettingsSection>
  );
}
